import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { importedPostsApi, postApi, analyzeApi, gapsApi, onboardingApi } from './services/api';
import { trackPageView } from './services/analytics';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Alert,
  Fab,
  Snackbar,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

import ErrorBoundary from './components/ErrorBoundary';
import RequireAuth, { LoginPage } from './components/RequireAuth';
import SEO from './components/SEO';
import MainLayout from './components/MainLayout';
import Dashboard from './components/dashboard';
import UpgradeModal from './components/UpgradeModal';
import AnalyzeContent from './components/AnalyzeContent';
import RankContent from './components/RankContent';
import TrendingNow from './components/trends';
import PostManagementPage from './pages/PostManagementPage';
import FindGaps from './components/FindGaps';
import GenerateIdeas from './components/GenerateIdeas';
import EngagementHub from './components/EngagementHub';
import LeadCRM from './components/LeadCRM';
import Guide from './components/Guide';
import Settings from './components/Settings';
import PastPosts from './components/PastPosts';
import Pricing from './components/Pricing';
import Billing from './components/Billing';
import UploadTracker from './components/media/UploadTracker';
import OnboardingModal from './components/OnboardingModal';
import GlobalLoader from './components/GlobalLoader';

// Admin Pages & Providers
import { FeatureToggleProvider } from './contexts/FeatureToggleContext';
import RequireSuperAdmin from './components/common/RequireSuperAdmin';
import AdminToggles from './pages/AdminToggles';
import AdminPlans from './pages/AdminPlans';
import AdminNewsletter from './pages/AdminNewsletter';
import AdminBlog from './pages/AdminBlog';
import AdminR2Settings from './pages/AdminR2Settings';
import AdminGatewaySettings from './pages/AdminGatewaySettings';
import AdminAISettings from './pages/AdminAISettings';
import AdminPlatformSettings from './pages/AdminPlatformSettings';
import AdminIdeaTemplates from './pages/AdminIdeaTemplates';
import AdminDeadLetter from './pages/AdminDeadLetter';
import AdminInfrastructure from './pages/AdminInfrastructure';

// Public Pages
import LandingPage from './components/public/LandingPage';
import Terms from './components/public/Terms';
import Privacy from './components/public/Privacy';
import RefundPolicy from './components/public/RefundPolicy';
import CancellationPolicy from './components/public/CancellationPolicy';
import NewsletterVerify from './pages/NewsletterVerify';
import NewsletterPreferences from './pages/NewsletterPreferences';
import NewsletterUnsubscribed from './pages/NewsletterUnsubscribed';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';

// Data imported from backend conditionally

// Create light theme
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#22C55E', light: '#4ADE80', dark: '#16A34A' },
    secondary: { main: '#F1F5F9', light: '#F8FAFC', dark: '#E2E8F0' },
    background: { default: '#F8FAFC', paper: '#FFFFFF' },
    text: { primary: '#0F172A', secondary: '#475569' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    h1: { fontFamily: '"DM Sans", "Inter", sans-serif', fontWeight: 700, letterSpacing: '-0.03em' },
    h2: { fontFamily: '"DM Sans", "Inter", sans-serif', fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontFamily: '"DM Sans", "Inter", sans-serif', fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontFamily: '"DM Sans", "Inter", sans-serif', fontWeight: 700 },
    h5: { fontFamily: '"DM Sans", "Inter", sans-serif', fontWeight: 700 },
    h6: { fontFamily: '"DM Sans", "Inter", sans-serif', fontWeight: 700 },
    button: { fontFamily: '"DM Sans", "Inter", sans-serif', fontWeight: 700, textTransform: 'none', letterSpacing: '0.01em' },
  },
  shape: { borderRadius: 16 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: "#cbd5e1 transparent",
          "&::-webkit-scrollbar, & *::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
            borderRadius: 8,
            backgroundColor: "#cbd5e1",
            minHeight: 24,
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.4)',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.02)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 12, padding: '10px 20px', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)' },
        contained: { boxShadow: '0 0 15px rgba(34, 197, 94, 0.3)', '&:hover': { boxShadow: '0 0 25px rgba(34, 197, 94, 0.5)', transform: 'translateY(-1px)' } },
      },
    },
  },
});

// Create dark theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#22C55E', light: '#4ADE80', dark: '#16A34A' },
    secondary: { main: '#1E293B', light: '#334155', dark: '#0F172A' },
    background: { default: '#020617', paper: '#0F172A' },
    text: { primary: '#F8FAFC', secondary: '#94A3B8' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    h1: { fontFamily: '"DM Sans", "Inter", sans-serif', fontWeight: 700, letterSpacing: '-0.03em' },
    h2: { fontFamily: '"DM Sans", "Inter", sans-serif', fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontFamily: '"DM Sans", "Inter", sans-serif', fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontFamily: '"DM Sans", "Inter", sans-serif', fontWeight: 700 },
    h5: { fontFamily: '"DM Sans", "Inter", sans-serif', fontWeight: 700 },
    h6: { fontFamily: '"DM Sans", "Inter", sans-serif', fontWeight: 700 },
    button: { fontFamily: '"DM Sans", "Inter", sans-serif', fontWeight: 700, textTransform: 'none', letterSpacing: '0.01em' },
  },
  shape: { borderRadius: 16 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: "#334155 transparent",
          "&::-webkit-scrollbar, & *::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
            borderRadius: 8,
            backgroundColor: "#334155",
            minHeight: 24,
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(30, 41, 59, 0.4)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
             backgroundColor: 'rgba(30, 41, 59, 0.6)',
             boxShadow: '0 25px 30px -5px rgba(0, 0, 0, 0.4)',
             transform: 'translateY(-2px)'
          }
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 12, padding: '10px 20px', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)' },
        contained: { boxShadow: '0 0 15px rgba(34, 197, 94, 0.3)', '&:hover': { boxShadow: '0 0 25px rgba(34, 197, 94, 0.5)', transform: 'translateY(-1px)' } },
      },
    },
  },
});

// Plans that grant Pro features (must match backend config.PRO_MODEL_PLANS)
const PRO_PLANS = new Set(['growth', 'pro', 'agency', 'growth_usd', 'pro_usd', 'agency_usd', 'premium']);

// Utility to scroll to top, clear state, and fire page_view on every navigation
const LocationWatcher = ({ clearError }) => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    clearError();
    trackPageView(pathname, document.title);
  }, [pathname, clearError]);
  return null;
};

// Renders OnboardingModal only when inside /app/* — uses useLocation so it's reactive
const OnboardingGuard = ({ show, onComplete, onSkip }) => {
  const { pathname } = useLocation();
  if (!pathname.startsWith('/app')) return null;
  return <OnboardingModal open={show} onComplete={onComplete} onSkip={onSkip} />;
};

// Renders children only when inside /app/* — reactive via useLocation
const InAppOnly = ({ children }) => {
  const { pathname } = useLocation();
  if (!pathname.startsWith('/app')) return null;
  return children;
};

/**
 * BlogServerRedirect — immediately sends the browser to the Flask-rendered
 * blog HTML on the backend domain, preserving path + query string.
 * Without this, React Router catches /blog/* and renders a blank page.
 */
function BlogServerRedirect() {
  useEffect(() => {
    const backendUrl = process.env.REACT_APP_API_URL || 'https://api.socialixer.com';
    window.location.replace(backendUrl + window.location.pathname + window.location.search);
  }, []);
  return null;
}

function App() {
  const [analytics, setAnalytics] = useState([]);
  const [analysis, setAnalysis] = useState({});
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('theme') !== 'light');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isPro, setIsPro] = useState(false);

  const toggleTheme = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      localStorage.setItem('theme', next ? 'dark' : 'light');
      return next;
    });
  };

  const handleError = useCallback((err) => {
    if (err.isQuotaError) {
      setShowUpgradeModal(true);
      setError('');
    } else if (err.isFeatureDisabled) {
      // Feature is intentionally disabled — silently swallow, page shows empty state
    } else {
      setError(err.message || 'An unexpected error occurred.');
    }
  }, []);

  const theme = isDarkMode ? darkTheme : lightTheme;

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Use the same imported-posts dashboard endpoint as the platform tabs so
      // the Overview and per-platform views always show consistent, user-scoped data.
      const data = await importedPostsApi.getDashboard({});
      setAnalytics(data?.platforms_summary || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const token = await user.getIdTokenResult();
          setIsPro(PRO_PLANS.has(token.claims.plan));
        } catch (_) {
          setIsPro(false);
        }
        fetchAnalytics();
        // Check if new user needs onboarding
        onboardingApi.get().then((result) => {
          if (!result?.onboarding_completed) {
            setShowOnboarding(true);
          }
        }).catch(() => {
          // Non-critical — don't block the app
        });
      } else {
        setIsPro(false);
        setShowOnboarding(false);
      }
    });
    return () => unsubscribe();
  }, [fetchAnalytics]);

  /**
   * handlePost — called by PostContent with per-platform media files.
   *
   * mediaFiles = { common: File|null, [platform]: File|null }
   *   - "common" is the shared fallback file
   *   - per-platform keys override the shared file for that specific platform
   *
   * Returns the results[] array so PostContent can update the modal.
   * Does NOT manage loading state (PostContent owns posting UX).
   */
  const handlePost = useCallback(async (posts, mediaFiles = {}) => {
    setError('');
    try {
      // Build multipart/form-data
      const formData = new FormData();

      // Strip File objects before serialising posts to JSON
      const postsData = posts.map(p => ({ platform: p.platform, content: p.content }));
      formData.append('posts', JSON.stringify(postsData));

      // Shared / fallback media file
      if (mediaFiles.common) {
        formData.append('media', mediaFiles.common);
      }

      // Per-platform media overrides: appended as media_youtube, media_twitter, etc.
      for (const [key, file] of Object.entries(mediaFiles)) {
        if (key !== 'common' && file instanceof File) {
          formData.append(`media_${key}`, file);
        }
      }

      const { results } = await postApi.post(formData);

      // Show top-level snackbar summary
      const successCount = results.filter(r => r.status === 'success').length;
      if (successCount > 0) {
        setSuccessMessage(
          `Posted to ${successCount} platform${successCount > 1 ? 's' : ''}!`
        );
        // Notify PastPosts (and any other listeners) so they auto-refresh
        window.dispatchEvent(new CustomEvent('postPublished', {
          detail: { platforms: results.filter(r => r.status === 'success').map(r => r.platform) },
        }));
      }

      // Return results so PostContent can update the modal
      return results;
    } catch (err) {
      console.error('Error posting:', err);
      handleError(err);
      return [];
    }
  }, [handleError]);

  const handleAnalyze = useCallback(async (textInput, platform = null) => {
    setLoading(true);
    setError('');
    try {
      const data = await analyzeApi.analyze(textInput, platform);
      setAnalysis(data);
    } catch (error) {
      console.error('Error analyzing:', error);
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

const handleGaps = useCallback(async (currentContent, topContent, opts = {}) => {
    setLoading(true);
    setError('');
    try {
      // Store the FULL structured result — FindGaps renders all dimensions
      const data = await gapsApi.analyze(currentContent, topContent, opts);
      setGaps(data);
    } catch (error) {
      console.error('Error finding gaps:', error);
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const handleCloseSnackbar = useCallback(() => {
    setSuccessMessage('');
  }, []);

  const clearGlobalError = useCallback(() => {
    setError('');
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalLoader />
        <Router>
          <LocationWatcher clearError={clearGlobalError} />
          <Routes>
            {/* Blog — hard redirect to Flask server-rendered HTML (SEO/GEO) */}
            <Route path="/blog" element={<BlogServerRedirect />} />
            <Route path="/blog/*" element={<BlogServerRedirect />} />

            {/* Public Marketing Routes */}
            <Route path="/" element={<><SEO path="/" /><LandingPage /></>} />
            <Route path="/terms" element={<><SEO title="Terms of Service | Socialixer" description="Socialixer Terms of Service — read our terms for using the AI social media automation platform." path="/terms" noIndex={false} /><Terms /></>} />
            <Route path="/privacy" element={<><SEO title="Privacy Policy | Socialixer" description="Socialixer Privacy Policy — how we collect, use, and protect your data." path="/privacy" noIndex={false} /><Privacy /></>} />
            <Route path="/refund" element={<><SEO title="Refund Policy | Socialixer" description="Socialixer Refund Policy — refund windows, eligibility, and how to request a refund." path="/refund" noIndex={false} /><RefundPolicy /></>} />
            <Route path="/cancellation" element={<><SEO title="Cancellation Policy | Socialixer" description="Socialixer Cancellation Policy — how to cancel your subscription and what happens after." path="/cancellation" noIndex={false} /><CancellationPolicy /></>} />
            <Route path="/newsletter/verify" element={<><SEO title="Verify Email | Socialixer" description="Confirm your Socialixer waitlist spot." path="/newsletter/verify" noIndex /><NewsletterVerify /></>} />
            <Route path="/newsletter/preferences" element={<><SEO title="Email Preferences | Socialixer" description="Manage your Socialixer newsletter preferences." path="/newsletter/preferences" noIndex /><NewsletterPreferences /></>} />
            <Route path="/newsletter/unsubscribed" element={<><SEO title="Unsubscribed | Socialixer" description="You have been unsubscribed from Socialixer." path="/newsletter/unsubscribed" noIndex /><NewsletterUnsubscribed /></>} />

            {/* Redirect Google OAuth Callback from /settings to /app/settings */}
            <Route path="/settings" element={
              <Navigate to={`/app/settings${window.location.search}`} replace />
            } />
            <Route path="/billing" element={
              <Navigate to={`/app/billing${window.location.search}`} replace />
            } />

            {/* Private Application Routes — protected by RequireAuth */}
            <Route path="/app/*" element={
              <RequireAuth>
                <FeatureToggleProvider>
                  <MainLayout isDarkMode={isDarkMode} onToggleTheme={toggleTheme}>
                    <Routes>
                      <Route path="/" element={
                        <><SEO title="Analytics Dashboard | Socialixer" description="Cross-platform social media analytics dashboard." path="/app" noIndex breadcrumbs={[{ name: 'Dashboard', path: '/app' }]} />
                          <Dashboard analytics={analytics} loading={loading} onRefresh={fetchAnalytics} error={error} /></>
                      } />
                      <Route path="/calendar" element={
                        <PostManagementPage onPost={handlePost} error={error} />
                      } />
                      <Route path="/post" element={
                        <PostManagementPage onPost={handlePost} error={error} />
                      } />
                      <Route path="/analyze" element={
                        <><SEO title="Analyse Content | Socialixer" description="Analyse your social media content for sentiment, readability, and engagement score." path="/app/analyze" noIndex breadcrumbs={[{ name: 'Analyse', path: '/app/analyze' }]} />
                          <AnalyzeContent onAnalyze={handleAnalyze} loading={loading} analysis={analysis} error={error} /></>
                      } />
                      <Route path="/rank" element={
                        <><SEO title="Rank Content | Socialixer" description="Rank your published posts by engagement score across all connected platforms." path="/app/rank" noIndex breadcrumbs={[{ name: 'Rank', path: '/app/rank' }]} />
                          <RankContent /></>
                      } />
                      <Route path="/trends" element={
                        <><SEO title="Trending Now | Socialixer" description="Real-time trending topics from Google Trends to fuel your content strategy." path="/app/trends" noIndex breadcrumbs={[{ name: 'Trends', path: '/app/trends' }]} />
                          <TrendingNow /></>
                      } />
                      <Route path="/gaps" element={
                        <><SEO title="Content Gap Analysis | Socialixer" description="Discover content gaps between your posts and top-performing competitor content with AI analysis." path="/app/gaps" noIndex breadcrumbs={[{ name: 'Gap Analysis', path: '/app/gaps' }]} />
                          <FindGaps onFindGaps={handleGaps} loading={loading} gaps={gaps} error={error} isPro={isPro} /></>
                      } />
                      <Route path="/ideas" element={
                        <><SEO title="Generate Content Ideas | Socialixer" description="Generate AI-powered content ideas for Instagram, Twitter, LinkedIn and more using Google Gemini." path="/app/ideas" noIndex breadcrumbs={[{ name: 'Content Ideas', path: '/app/ideas' }]} />
                          <GenerateIdeas isPro={isPro} /></>
                      } />
                      <Route path="/engagement" element={
                        <><SEO title="Engagement Hub | Socialixer" description="Manage automated social media engagement and comment automation." path="/app/engagement" noIndex breadcrumbs={[{ name: 'Engagement Hub', path: '/app/engagement' }]} />
                          <EngagementHub /></>
                      } />
                      <Route path="/crm" element={
                        <><SEO title="Lead CRM | Socialixer" description="Manage social media leads detected from comments and messages." path="/app/crm" noIndex breadcrumbs={[{ name: 'Lead CRM', path: '/app/crm' }]} />
                          <LeadCRM /></>
                      } />
                      <Route path="/guide" element={
                        <><SEO title="Setup Guide | Socialixer" description="Step-by-step guide to connecting your social media platforms to Socialixer." path="/app/guide" noIndex breadcrumbs={[{ name: 'Guide', path: '/app/guide' }]} />
                          <Guide /></>
                      } />
                      <Route path="/settings" element={
                        <><SEO title="Platform Settings | Socialixer" description="Connect and manage your social media platform API credentials." path="/app/settings" noIndex breadcrumbs={[{ name: 'Settings', path: '/app/settings' }]} />
                          <Settings /></>
                      } />
                      <Route path="/past-posts" element={
                        <><SEO title="Past Posts | Socialixer" description="Browse and sync your published social media posts." path="/app/past-posts" noIndex breadcrumbs={[{ name: 'Past Posts', path: '/app/past-posts' }]} />
                          <PastPosts /></>
                      } />
                      <Route path="/pricing" element={
                        <><SEO title="Pricing & Plans | Socialixer" description="Upgrade your Socialixer plan" path="/app/pricing" noIndex breadcrumbs={[{ name: 'Pricing & Plans', path: '/app/pricing' }]} />
                          <Pricing /></>
                      } />
                      <Route path="/billing" element={
                        <><SEO title="Billing | Socialixer" description="Manage your subscription" path="/app/billing" noIndex breadcrumbs={[{ name: 'Billing', path: '/app/billing' }]} />
                          <Billing /></>
                      } />

                      {/* Admin Area */}
                      <Route path="/admin/toggles" element={
                        <RequireSuperAdmin>
                          <><SEO title="Admin | Socialixer" description="Super Admin features management." path="/app/admin/toggles" noIndex breadcrumbs={[{ name: 'Admin Toggles', path: '/app/admin/toggles' }]} />
                            <AdminToggles /></>
                        </RequireSuperAdmin>
                      } />
                      <Route path="/admin/plans" element={
                        <RequireSuperAdmin>
                          <><SEO title="Admin Plans | Socialixer" description="Manage subscription plans and quota limits." path="/app/admin/plans" noIndex breadcrumbs={[{ name: 'Plan Management', path: '/app/admin/plans' }]} />
                            <AdminPlans /></>
                        </RequireSuperAdmin>
                      } />
                      <Route path="/admin/newsletter" element={
                        <RequireSuperAdmin>
                          <><SEO title="Newsletter Admin | Socialixer" description="Manage newsletter subscribers, stats, and broadcasts." path="/app/admin/newsletter" noIndex breadcrumbs={[{ name: 'Newsletter Admin', path: '/app/admin/newsletter' }]} />
                            <AdminNewsletter /></>
                        </RequireSuperAdmin>
                      } />
                      <Route path="/admin/blog" element={
                        <RequireSuperAdmin>
                          <><SEO title="Blog Admin | Socialixer" description="Create and manage blog posts for SEO and content marketing." path="/app/admin/blog" noIndex breadcrumbs={[{ name: 'Blog Admin', path: '/app/admin/blog' }]} />
                            <AdminBlog /></>
                        </RequireSuperAdmin>
                      } />
                      <Route path="/admin/r2-settings" element={
                        <RequireSuperAdmin>
                          <><SEO title="R2 Settings | Socialixer" description="Configure Cloudflare R2 storage credentials." path="/app/admin/r2-settings" noIndex breadcrumbs={[{ name: 'R2 Settings', path: '/app/admin/r2-settings' }]} />
                            <AdminR2Settings /></>
                        </RequireSuperAdmin>
                      } />
                      <Route path="/admin/gateway-settings" element={
                        <RequireSuperAdmin>
                          <><SEO title="Gateway Settings | Socialixer" description="Configure Stripe and Razorpay payment gateway credentials." path="/app/admin/gateway-settings" noIndex breadcrumbs={[{ name: 'Gateway Settings', path: '/app/admin/gateway-settings' }]} />
                            <AdminGatewaySettings /></>
                        </RequireSuperAdmin>
                      } />
                      <Route path="/admin/ai-settings" element={
                        <RequireSuperAdmin>
                          <><SEO title="AI Settings | Socialixer" description="Configure AI provider (Gemini, OpenAI, Claude, Grok)." path="/app/admin/ai-settings" noIndex breadcrumbs={[{ name: 'AI Settings', path: '/app/admin/ai-settings' }]} />
                            <AdminAISettings /></>
                        </RequireSuperAdmin>
                      } />
                      <Route path="/admin/platform-settings" element={
                        <RequireSuperAdmin>
                          <><SEO title="Platform Settings | Socialixer" description="Configure Instagram, Facebook, and YouTube OAuth app credentials." path="/app/admin/platform-settings" noIndex breadcrumbs={[{ name: 'Platform Settings', path: '/app/admin/platform-settings' }]} />
                            <AdminPlatformSettings /></>
                        </RequireSuperAdmin>
                      } />
                      <Route path="/admin/idea-templates" element={
                        <RequireSuperAdmin>
                          <><SEO title="Idea Templates | Socialixer" description="Manage AI-powered idea generation templates per niche." path="/app/admin/idea-templates" noIndex breadcrumbs={[{ name: 'Idea Templates', path: '/app/admin/idea-templates' }]} />
                            <AdminIdeaTemplates /></>
                        </RequireSuperAdmin>
                      } />
                      <Route path="/admin/dead-letter" element={
                        <RequireSuperAdmin>
                          <><SEO title="Dead-Letter Queue | Socialixer" description="Manage failed comment-queue tasks that exhausted auto-retries." path="/app/admin/dead-letter" noIndex breadcrumbs={[{ name: 'Dead-Letter Queue', path: '/app/admin/dead-letter' }]} />
                            <AdminDeadLetter /></>
                        </RequireSuperAdmin>
                      } />
                      <Route path="/admin/infrastructure" element={
                        <RequireSuperAdmin>
                          <><SEO title="Infrastructure | Socialixer" description="Switch between Render and Railway hosting platforms." path="/app/admin/infrastructure" noIndex breadcrumbs={[{ name: 'Infrastructure', path: '/app/admin/infrastructure' }]} />
                            <AdminInfrastructure /></>
                        </RequireSuperAdmin>
                      } />
                    </Routes>
                  </MainLayout>
                </FeatureToggleProvider>
              </RequireAuth>
            } />

            {/* Login convenience route */}
            <Route path="/login" element={<LoginPage />} />

            {/* Admin-only hidden login — not linked from UI; share URL directly */}
            <Route path="/secret-login" element={<LoginPage />} />

            {/* Catch-all: redirect any unknown URL to the landing page */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <UploadTracker />

          {currentUser && (
            <OnboardingGuard
              show={showOnboarding}
              onComplete={() => setShowOnboarding(false)}
              onSkip={() => setShowOnboarding(false)}
            />
          )}

          <InAppOnly>
            <Fab
              color="primary"
              aria-label="refresh"
              sx={{
                position: 'fixed',
                bottom: 32,
                right: 32,
                background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                boxShadow: '0 8px 16px -4px rgba(99, 102, 241, 0.5)',
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: '0 12px 24px -4px rgba(99, 102, 241, 0.6)',
                }
              }}
              onClick={fetchAnalytics}
            >
              <RefreshIcon />
            </Fab>
          </InAppOnly>

          <Snackbar
            open={!!successMessage}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity="success"
              sx={{
                bgcolor: theme.palette.success.main,
                color: 'white',
                borderRadius: 3,
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                '& .MuiAlert-icon': { color: 'white' }
              }}
            >
              {successMessage}
            </Alert>
          </Snackbar>

          <UpgradeModal
            open={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
          />
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;